/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import '../style/simpleInput.scss';

import React, {CSSProperties, PureComponent} from 'react';
import classNames from "classnames";
import {ControlContext} from "../../../util/controlContext";
import {colorIsWhite, convertColor, editColor} from "../../../util/colorUtil";
import {ElementPosition, OrnamentShape} from "../../../util/interfaces";
import AwesomeIcon from '../../../icon/awesomeIcon';
import {stopPropagation} from "../../../util/controlUtil";
import {DVRInputProps} from "../../v2/inputController_v2";

export default class SimpleInput extends PureComponent<DVRInputProps> {
    declare context: React.ContextType<typeof ControlContext>;
    static contextType = ControlContext;

    private input: HTMLInputElement | HTMLTextAreaElement | null = null;

    private onFocus = (evt: React.FocusEvent) => {
        const {onKeyDown, autoSelect, value, onFocus} = this.props;
        if (this.input) {
            if (onKeyDown)
                this.input.addEventListener('keydown', onKeyDown as any);
            if (autoSelect && value.toString().length) {
                this.input.setSelectionRange(0, value.toString().length);
            }
        }
        onFocus(evt);
    };

    private onBlur = (evt: React.FocusEvent) => {
        const {onKeyDown, onBlur} = this.props;
        if (this.input) this.input.removeEventListener('keydown', onKeyDown as any);
        onBlur(evt);
    };

    private getBaseColor = (trueBase: boolean = false): string => {
        const themeColor = this.context.baseColor, {baseColor} = this.props;
        let color: string;
        if (baseColor) color = baseColor;
        else color = themeColor;
        if (colorIsWhite(color) && !trueBase) return this.getContrastColor(false);
        return convertColor(color);
    };

    private getContrastColor = (allowWhite: boolean = true): string => {
        const base = this.getBaseColor(true);
        if (colorIsWhite(base) && allowWhite) return base;

        const themeColor = this.context.contrastColor, {contrastColor} = this.props;
        let color: string;
        if (contrastColor && contrastColor.length) color = contrastColor;
        else color = themeColor;
        return convertColor(color);
    };

    private getBorderColor = (): string => {
        const {isHovered, isFocused, disabled, error, borderColor} = this.props;
        let color: string;
        if (error) color = 'red';
        else if (disabled) color = 'color-gray-4';
        else if (isFocused) color = this.getBaseColor();
        else if (isHovered) color = editColor(.3, this.getBaseColor());
        else color = borderColor || this.context.borderColor || 'color-gray-3';
        return convertColor(color);
    };

    private getCurrentColor = (): string => {
        const {isHovered, isFocused, disabled, error} = this.props;
        let color: string;
        if (error) color = 'red';
        else if (disabled) color = 'color-gray-4';
        else if (isFocused) color = this.getBaseColor();
        else if (isHovered) color = editColor(.3, this.getBaseColor());
        else color = 'color-gray-3';
        return convertColor(color);
    };

    private getContainerStyle = (): CSSProperties => ({
        borderColor: this.getBorderColor(),
        ...this.props.containerStyle,
    });

    private getLabelStyle = (): CSSProperties => ({
        color: this.getCurrentColor(),
        ...this.props.labelStyle,
    });

    private getInputStyle = (): CSSProperties => ({
        ...this.props.inputStyle,
    });

    private getInputProps = () => {
        const {placeholder, inputProps, onChange, value, id, disabled, readOnly} = this.props;
        return {
            ...inputProps,
            className: classNames('dvr-simple-input', inputProps?.className),
            ref: this.attachRef,
            onBlur: this.onBlur,
            onFocus: this.onFocus,
            style: this.getInputStyle(),
            placeholder,
            onChange,
            value,
            id: `${id}-input`,
            // type,
            disabled,
            readOnly,
        };
    };

    private attachRef = (ref: HTMLInputElement | HTMLTextAreaElement | null) => {
        this.input = ref;
    };

    private getOrnament = (placement: ElementPosition): OrnamentShape | null => {
        const {ornaments, children} = this.props;
        if (ornaments !== undefined) {
            if (Array.isArray(ornaments)) {
                for (const orn of ornaments)
                    if (orn.placement === placement) return orn;
            } else if (ornaments.placement === placement) return ornaments;

            if (Array.isArray(children)) {
                for (const child of children)
                    if (child && child.hasOwnProperty('placement') && (child as OrnamentShape).placement ===
                        placement) return child as OrnamentShape;
            } else if (children && children.hasOwnProperty('placement') && (children as OrnamentShape).placement ===
                placement) return children as OrnamentShape;
        }
        return null;
    };

    private renderOrnament = (placement: ElementPosition) => {
        const {onClick, clearAble, value, onClear, disabled} = this.props;
        let toRender: OrnamentShape | null = this.getOrnament(placement);
        if (!toRender && placement === ElementPosition.RIGHT && clearAble && !disabled)
            toRender = {
                allowPropagation: false,
                element: <AwesomeIcon name='eraser' onClick={onClear} className={classNames('dvr-simple-input-clear',
                    !value.toString().length && 'invis')}/>,
                placement
            };
        if (!toRender) return null;
        else
            return (
                <div className={classNames('dvr-simple-input-ornament', onClick !== undefined && 'clickable')}
                     onClick={toRender.allowPropagation ? onClick : stopPropagation}>
                    {toRender.element}
                </div>
            )
    };

    render = () => {
        const {
            className, label, labelClassName, onMouseEnter, onMouseLeave, area, onClick, fullWidth, error, errorClassName,
            errorStyle, disabled, id
        } = this.props;
        return (
            <div className={classNames('dvr-simple-input-container', fullWidth && 'full-width',
                error !== null && 'error', disabled && 'disabled', className)}
                 style={this.getContainerStyle()} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
                 onClick={onClick} id={id}>
                <label className={classNames('dvr-simple-input-label', labelClassName)}
                       style={this.getLabelStyle()}>{label}</label>
                {this.renderOrnament(ElementPosition.LEFT)}
                {area ? <textarea {...this.getInputProps()}/> : <input {...this.getInputProps()}/>}
                {this.renderOrnament(ElementPosition.RIGHT)}
                {error !== null &&
                <label className={classNames('dvr-simple-input-error', errorClassName)}
                       style={errorStyle}>{error}</label>}
            </div>
        );
    }
}